/**
 * Login-time MFA step-up helpers.
 *
 * Card: "MFA (TOTP) + Passkeys (WebAuthn)" -- https://trello.com/c/lf2Z8aOo
 * Backend: POST /auth/login (the gate) + POST /auth/mfa/verify.
 *
 * Extracted from TheLoginPage.vue so the two decisions that matter --
 * "is this 401 a step-up or a failed login?" and "is this response
 * actually a completed session?" -- are testable on their own, rather
 * than only reachable through a Vuetify form with a router and a store
 * attached.
 *
 * The GATE IS SERVER-SIDE. Nothing here enforces anything: the server
 * withholds the token, and this module only interprets that. A client
 * that ignored all of this would still hold no session, which is the
 * property that makes the feature real. See
 * api/tests/test_mfa_service.py for the enforcement tests.
 */
import { verifyMfaChallenge } from '@/services/securityService'

export const mfaStepUp = {
  /**
   * True when a login rejection is really "password OK, second factor
   * needed" rather than "bad credentials".
   *
   * Requires BOTH the flag and a challenge. A flag without a challenge
   * is unusable -- there would be nothing to send to the verify route --
   * so treating it as a step-up would strand the user on a prompt that
   * can never succeed. Better to fall through to the ordinary
   * failed-login path.
   */
  isStepUp(err) {
    const data = err?.response?.data
    return (
      err?.response?.status === 401 &&
      !!data?.mfa_required &&
      typeof data?.mfa_challenge === 'string' &&
      data.mfa_challenge.length > 0
    )
  },

  challengeOf(err) {
    return err?.response?.data?.mfa_challenge || ''
  },

  /**
   * Exchange a challenge + second factor for the real auth payload.
   *
   * Accepts a TOTP code or a recovery code; the server decides which it
   * is. Trimmed because both arrive by paste as often as by typing, and
   * a trailing space would otherwise read to the user as a wrong code.
   *
   * Throws when the payload carries no token: the caller stores whatever
   * this resolves to, so a malformed 200 must not be mistaken for a
   * completed login and leave a half-authenticated client behind.
   */
  async complete(challenge, code) {
    const trimmed = (code || '').trim()
    if (!trimmed) {
      throw new Error('Enter the code from your authenticator app.')
    }

    const payload = await verifyMfaChallenge(challenge, trimmed)
    if (!payload?.token) {
      throw new Error('That code could not be verified. Please try again.')
    }
    return payload
  },
}

export default mfaStepUp
